(() => {
  'use strict';

  if (window.AWCompatibilityIndex?.version) return;

  const normalize = (value = '') => String(value || '').trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const urlKey = (value = '') => {
    try { return new URL(value, location.origin).pathname.replace(/\/$/, ''); }
    catch (_) { return String(value || '').split('?')[0].replace(/\/$/, ''); }
  };

  const cloneSourceContent = (source) => {
    if (!source) return document.createDocumentFragment();
    if (source.content) return source.content.cloneNode(true);
    const fragment = document.createDocumentFragment();
    source.childNodes.forEach((node) => fragment.appendChild(node.cloneNode(true)));
    return fragment;
  };

  const mergeFallback = (source, current, selector) => {
    if (!source || !current) return 0;
    const known = new Set(
      Array.from(source.querySelectorAll(selector)).map((node) => String(node.dataset.productId || '')).filter(Boolean)
    );
    let added = 0;
    current.querySelectorAll(selector).forEach((node) => {
      const id = String(node.dataset.productId || '');
      if (!id || known.has(id)) return;
      known.add(id);
      source.appendChild(node.cloneNode(true));
      added += 1;
    });
    return added;
  };

  const fallbackLogistics = (node) => {
    if (!node) return { label: '', delay: '', state: '' };
    const explicitLabel = String(node.dataset.logisticsLabel || '').trim();
    if (explicitLabel) {
      return {
        label: explicitLabel,
        delay: String(node.dataset.logisticsDelay || '').trim(),
        state: String(node.dataset.logisticsState || '').trim()
      };
    }
    const tags = normalize(node.dataset.tags || '');
    const type = normalize(node.dataset.productType || '');
    if ((type.includes('service') || type.includes('atelier')) && (tags.includes('atelier-airsoft') || tags.includes('aw-atelier-p1'))) {
      return { label: 'Préparation Atelier', delay: '1 à 2 semaines', state: 'atelier' };
    }
    if (tags.includes('supplier-ops-store') || tags.includes('aw-ops-step6-core')) {
      return { label: 'Disponible fournisseur', delay: '1 à 4 jours', state: 'supplier' };
    }
    if (tags.includes('aw-colombi-auto') || tags.includes('colombi-sports')) {
      return { label: 'Disponible fournisseur', delay: '1 à 2 semaines', state: 'supplier' };
    }
    if (tags.includes('dsers')) {
      return { label: 'Disponible fournisseur', delay: '1 à 4 semaines', state: 'supplier' };
    }
    if (node.dataset.available === 'false') return { label: 'Indisponible', delay: '', state: 'unavailable' };
    return { label: 'Disponible', delay: '', state: 'available' };
  };

  const ensureLogisticsStyles = () => {
    if (document.getElementById('AwCompatibilityPublicLogisticsCss')) return;
    const style = document.createElement('style');
    style.id = 'AwCompatibilityPublicLogisticsCss';
    style.textContent = '.aw-compatibility-search__logistics{display:grid;gap:.2rem;margin:.1rem 0 1.2rem;color:var(--aw-search-muted);font-size:1.05rem;line-height:1.4}.aw-compatibility-search__logistics strong{color:var(--aw-search-text);font-size:1rem;letter-spacing:.05em;text-transform:uppercase}.aw-compatibility-search__logistics small{font-size:1.05rem}';
    document.head.appendChild(style);
  };

  const logisticsMap = (root) => {
    const map = new Map();
    root?.querySelectorAll('[data-aw-product-source] [data-aw-product]').forEach((node) => {
      const key = urlKey(node.dataset.url || '');
      if (key) map.set(key, fallbackLogistics(node));
    });
    return map;
  };

  const decorateSearchLogistics = (root) => {
    const grid = root?.querySelector('[data-aw-products]');
    if (!grid) return;
    const map = logisticsMap(root);
    grid.querySelectorAll('.aw-compatibility-search__product').forEach((card) => {
      const link = card.querySelector('h3 a[href],.aw-compatibility-search__product-media[href]');
      const profile = link ? map.get(urlKey(link.href)) : null;
      const old = card.querySelector('[data-aw-public-logistics]');
      if (!profile?.label) { old?.remove(); return; }
      const signature = `${profile.state}|${profile.label}|${profile.delay}`;
      if (old?.dataset.awLogisticsSignature === signature) return;
      old?.remove();
      const body = card.querySelector('.aw-compatibility-search__product-body');
      const footer = card.querySelector('.aw-compatibility-search__product-footer');
      if (!body) return;
      const block = document.createElement('div');
      block.className = 'aw-compatibility-search__logistics';
      block.dataset.awPublicLogistics = 'true';
      block.dataset.awLogisticsSignature = signature;
      block.dataset.awLogisticsState = profile.state || '';
      const strong = document.createElement('strong');
      strong.textContent = profile.label;
      block.append(strong);
      if (profile.delay) {
        const small = document.createElement('small');
        small.textContent = `Délai estimé : ${profile.delay}`;
        block.append(small);
      }
      body.insertBefore(block, footer || null);
    });
    root.dataset.awPublicLogistics = 'ready';
  };

  const installSearchLogistics = (root) => {
    const grid = root?.querySelector('[data-aw-products]');
    if (!grid) return;
    ensureLogisticsStyles();
    const run = () => queueMicrotask(() => decorateSearchLogistics(root));
    run();
    if (grid.dataset.awLogisticsObserver !== 'true' && 'MutationObserver' in window) {
      grid.dataset.awLogisticsObserver = 'true';
      let pending = false;
      new MutationObserver(() => {
        if (pending) return;
        pending = true;
        queueMicrotask(() => { pending = false; decorateSearchLogistics(root); });
      }).observe(grid, { childList: true });
    }
    if (root.dataset.awLogisticsEvents !== 'true') {
      root.dataset.awLogisticsEvents = 'true';
      ['aw:compatibility-index-ready','aw:compatibility-changed','aw:context-changed'].forEach((name) => window.addEventListener(name, run));
    }
  };

  const exactModelContext = () => {
    const root = document.querySelector('[data-aw-compatibility-search][data-model-page="true"]');
    const node = document.querySelector('[data-aw-seo-model]');
    if (!root || !node) return null;
    const manufacturerLabel = String(node.dataset.manufacturer || '').trim();
    const model = String(node.dataset.model || '').trim();
    const platform = normalize(node.dataset.platform || '');
    const replicaType = normalize(node.dataset.replicaType || '');
    if (!platform) return null;
    return {
      model,
      modelLabel: `${manufacturerLabel} ${model}`.trim(),
      platform,
      platformLabel: String(node.dataset.platformLabel || platform),
      manufacturer: normalize(manufacturerLabel),
      manufacturerLabel,
      replicaType,
      replicaTypeLabel: String(node.dataset.replicaTypeLabel || replicaType || '')
    };
  };

  const restoreExactModelContext = () => {
    const detail = exactModelContext();
    if (!detail) return false;
    try {
      localStorage.setItem('aw_selected_platform', detail.platform);
      if (detail.manufacturer) localStorage.setItem('aw_selected_manufacturer', detail.manufacturer);
      else localStorage.removeItem('aw_selected_manufacturer');
      if (detail.replicaType) localStorage.setItem('aw_selected_replica_type', detail.replicaType);
      else localStorage.removeItem('aw_selected_replica_type');
    } catch (_) {}

    if (window.AWAirsoftContext?.write) {
      window.AWAirsoftContext.write(detail, 'compatibility-model-page');
    } else {
      window.dispatchEvent(new CustomEvent('aw:platform-changed', { detail }));
      window.dispatchEvent(new CustomEvent('aw:compatibility-changed', { detail }));
    }
    const root = document.querySelector('[data-aw-compatibility-search][data-model-page="true"]');
    if (root) root.dataset.awExplicitModelContext = 'true';
    return true;
  };

  const installSearchIndex = (scope = document) => {
    const sourceTemplate = document.querySelector('[data-aw-precomputed-compatibility-source]');
    if (!sourceTemplate) return;

    const roots = [];
    if (scope.matches?.('[data-aw-compatibility-search]')) roots.push(scope);
    scope.querySelectorAll?.('[data-aw-compatibility-search]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.awIndexMode === 'precomputed-with-fallback') { installSearchLogistics(root); return; }
      const current = root.querySelector('[data-aw-product-source]');
      if (!current) return;

      const source = document.createElement('div');
      source.appendChild(cloneSourceContent(sourceTemplate));
      source.setAttribute('data-aw-product-source', '');
      source.dataset.currentPage = current.dataset.currentPage || '1';
      source.dataset.awFallbackNextPage = current.dataset.nextPage || '';
      source.dataset.nextPage = '';
      source.dataset.indexMode = 'precomputed-with-fallback';
      const fallbackCount = mergeFallback(source, current, '[data-aw-product]');
      current.replaceWith(source);

      const count = source.querySelectorAll('[data-aw-product]').length;
      root.dataset.awIndexMode = 'precomputed-with-fallback';
      root.dataset.awIndexCount = String(count);
      root.dataset.awFallbackSeedCount = String(fallbackCount);
      root.dataset.awFallbackPagination = 'deferred';
      installSearchLogistics(root);
      window.dispatchEvent(new CustomEvent('aw:compatibility-index-ready', {
        detail: { scope: 'search', mode: 'precomputed-with-fallback', count, fallbackCount }
      }));
    });
  };

  const installPdpIndex = (scope = document) => {
    const sourceTemplate = document.querySelector('[data-aw-precomputed-compatible-source]');
    if (!sourceTemplate) return;

    const roots = [];
    if (scope.matches?.('[data-aw-compatible-products]')) roots.push(scope);
    scope.querySelectorAll?.('[data-aw-compatible-products]').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (root.dataset.awIndexMode === 'precomputed') return;
      const grid = root.querySelector('[data-aw-products-grid]');
      if (!grid) return;

      grid.replaceChildren(cloneSourceContent(sourceTemplate));
      root.dataset.nextUrl = '';
      const count = grid.querySelectorAll('[data-aw-product-card]').length;
      root.dataset.awIndexMode = 'precomputed';
      root.dataset.awIndexCount = String(count);
      window.dispatchEvent(new CustomEvent('aw:compatibility-index-ready', {
        detail: { scope: 'pdp', mode: 'precomputed', count }
      }));
    });
  };

  const install = (scope = document) => {
    installSearchIndex(scope);
    installPdpIndex(scope);
  };

  window.addEventListener('aw:garage-context-changed', () => queueMicrotask(restoreExactModelContext));

  window.AWCompatibilityIndex = {
    version: '1.4.0',
    install,
    mode: 'precomputed-with-fallback',
    fallbackPagination: 'deferred',
    restoreExactModelContext,
    decorateSearchLogistics
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => install(), { once: true });
  } else {
    install();
  }

  document.addEventListener('shopify:section:load', (event) => install(event.target));
})();